import { UiSchema } from '@rjsf/utils'
import { useDispatch } from 'react-redux'
import { BotFeatures, PromptDefaultAnswerSchema } from '@feedyou/schema'

import { useIsTelephony } from '../../../store/flow'
import { useAccessLevel } from '../../../store/user'
import { useParentStep } from '../../Step/StepProvider'
import { HIDDEN_WIDGET } from '../../../features/schema'
import { modalsActions } from '../../../store/modals/modalsSlice'
import { DropdownMenuItem } from '../../design-system/DropdownMenu'
import { StepSettingsSubset } from '../../../constants/stepSettings'
import { isStepPreprocessorEnabled, isCustomStepPreprocessorEnabled } from './utils'
import { GetSubstepMenuItems } from '../../design-system/StepSubdialogPill/StepSubdialogPill'
import { createItemList } from '../../../lib/array'

type Params = {
  defaultAnswerItem: PromptDefaultAnswerSchema | undefined
  parentDialogId: string
  deleteAnswer(): void
}

// setSelectDialog needs to be injected deeper down, where the state is managed,
// this is why we return a getter from here
export function useGetDefaultAnswerMenuItems({
  defaultAnswerItem,
  parentDialogId,
  deleteAnswer
}: Params): GetSubstepMenuItems {
  const step = useParentStep()!
  const dispatch = useDispatch()
  const canEdit = useAccessLevel()?.edit

  return ({ changeDialog }) => {
    if (!defaultAnswerItem) return []
    return createItemList<DropdownMenuItem>([
      {
        condition: canEdit,
        type: 'text',
        icon: 'cog',
        label: 'Settings',
        className: '.e2e-menu-item-settings',
        dataTest: 'settings',
        onClick: () =>
          dispatch(
            modalsActions.openModal({
              type: 'step_settings',
              substepType: StepSettingsSubset.DEFAULT_ANSWER,
              openSection: 'form',
              dialogId: parentDialogId,
              stepOrSubstep: { type: step.type, ...defaultAnswerItem, questionId: step.id }
            })
          )
      },
      {
        condition: canEdit,
        type: 'text',
        icon: 'window-restore',
        label: 'Change dialog',
        className: '.e2e-menu-item-change-dialog',
        dataTest: 'change-dialog',
        onClick: changeDialog
      },
      {
        condition: canEdit,
        type: 'text',
        icon: 'trash',
        label: 'Delete',
        className: '.e2e-menu-item-delete',
        dataTest: 'delete',
        onClick: deleteAnswer
      }
    ])
  }
}

export function useBaseQuestionLikeStepUiSchema(sdkFeatures: BotFeatures[]): UiSchema {
  const uiSchema: UiSchema = {}
  const isTelephony = useIsTelephony()

  if (!isTelephony || !isStepPreprocessorEnabled(sdkFeatures)) {
    uiSchema.preprocessor = HIDDEN_WIDGET
    return uiSchema
  }

  if (!isCustomStepPreprocessorEnabled(sdkFeatures)) {
    uiSchema.preprocessor = { type: HIDDEN_WIDGET, customPreprocessor: HIDDEN_WIDGET }
    return uiSchema
  }

  uiSchema.preprocessor = {
    type: HIDDEN_WIDGET,
    customPreprocessor: {
      value: { 'ui:widget': 'EditorJavascriptWidget' },
      enabled: { 'ui:classNames': 'form-checkbox-move-right-by-column' }
    }
  }

  return uiSchema
}
